/**
 * <zg-mask-layers>: progressive enhancement for sections/zg-mask-layers.liquid.
 *
 * The section renders the finished regular-mask state: the first toggle pressed, nothing
 * selected, ply 02 uncoated, Replay hidden. This element adds
 * - the coated state: `is-coated` on this element, which CSS turns into the khaki ply 02, and the
 *   note text swapped from data-note-off / data-note-on;
 * - ply selection: a list button (keyboard and pointer) or a ply or marker in the artwork (pointer)
 *   selects that ply, and selecting it again clears the selection. The selected ply's marker and
 *   list number turn solid and, unless data-dim="false", the other plies fade. With nothing
 *   selected, the coated state highlights ply 02;
 * - the automatic switch: the first time 30% of the illustration is visible, the mask turns
 *   coated after AUTO_DEMO_DELAY, unless a shopper has already used the toggle or picked a ply.
 *   Replay switches back to the regular mask and runs it again.
 *
 * Ported from the design's React class ("ZenGUARD Mask Layers"). The design observes the whole
 * section; this observes the illustration, because the stacked phone layout can be too tall for
 * 30% of the section ever to be on screen. With prefers-reduced-motion there is no automatic
 * switch and no Replay; the toggle and selection still work, and CSS drops the transitions.
 * WCAG 2.2.2 doesn't apply: the switch is one colour change of under 1.2 s, not continuous motion.
 */

/** ms after the illustration comes into view before switching to the coated mask. */
const AUTO_DEMO_DELAY = 3200;
/** Share of the illustration that must be visible to queue the automatic switch. */
const DEMO_RATIO = 0.3;
/** The coated ply's index: fixed artwork (snippets/zg-mask-layers-art.liquid). */
const COATED_PLY = 1;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class ZgMaskLayers extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {HTMLElement | null} */
  #figure = null;
  /** @type {HTMLElement | null} */
  #note = null;
  /** @type {HTMLButtonElement[]} */
  #toggles = [];
  /** @type {HTMLButtonElement | null} */
  #replayButton = null;
  #coated = false;
  /** Selected ply index, or -1. */
  #selected = -1;
  /** A shopper used the toggle or picked a ply: the automatic switch no longer happens. */
  #touched = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #demoTimer;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#figure = this.querySelector('.zg-mask-layers__figure');
    this.#note = this.querySelector('.zg-mask-layers__note');
    this.#toggles = Array.from(this.querySelectorAll('button[data-coat]'));
    this.#replayButton = this.querySelector('button[data-replay]');
    this.#coated = this.classList.contains('is-coated');

    for (const button of this.#toggles) {
      button.addEventListener('click', () => this.#setCoated(button.dataset.coat === 'true', true), { signal });
    }
    for (const button of this.querySelectorAll('button.zg-mask-layers__ply-button[data-ply]')) {
      button.addEventListener('click', () => this.#pick(plyIndex(button)), { signal });
    }
    // Plies and markers in the artwork; pointer only, the list buttons are the keyboard route.
    this.#figure?.addEventListener('click', this.#onFigureClick, { signal });
    this.#replayButton?.addEventListener('click', () => this.replay(), { signal });
    reducedMotion.addEventListener('change', () => this.#restart(), { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    }

    this.#render();
    this.#start();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#stop();
  }

  /** Back to the regular mask with nothing selected, then switch to the coated mask again (the Replay button). */
  replay() {
    if (!this.#demoEnabled) return;

    clearTimeout(this.#demoTimer);
    this.#touched = false;
    this.#selected = -1;
    this.#applyCoated(false);
    this.#render();
    this.#queueDemo();
  }

  get #demoEnabled() {
    return this.dataset.autoDemo !== 'false' && !reducedMotion.matches;
  }

  #start() {
    const demo = this.#demoEnabled;
    if (this.#replayButton) this.#replayButton.hidden = !demo;
    if (!demo || !this.#figure) return;

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#handleIntersection, { threshold: DEMO_RATIO });
      this.#observer.observe(this.#figure);
    } else {
      this.#queueDemo();
    }
  }

  #stop() {
    this.#observer?.disconnect();
    this.#observer = null;
    clearTimeout(this.#demoTimer);
  }

  /** The reduced-motion preference changed: keep the current state, start again under the new preference. */
  #restart() {
    this.#stop();
    this.#start();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersection = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= DEMO_RATIO)) return;

    this.#observer?.disconnect();
    this.#observer = null;
    this.#queueDemo();
  };

  #queueDemo() {
    clearTimeout(this.#demoTimer);
    if (!this.#demoEnabled) return;

    this.#demoTimer = setTimeout(() => {
      if (!this.#touched) this.#setCoated(true, false);
    }, AUTO_DEMO_DELAY);
  }

  /** @param {MouseEvent} event */
  #onFigureClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('.zg-mask-layers__hit[data-ply]') : null;
    if (target) this.#pick(plyIndex(target));
  };

  /** Theme editor: selecting a ply block selects its ply. @param {Event} event */
  #onBlockSelect = (event) => {
    const item = event.target instanceof Element ? event.target.closest('.zg-mask-layers__ply[data-ply]') : null;
    if (!item || !this.contains(item)) return;

    this.#touched = true;
    this.#selected = plyIndex(item);
    this.#render();
  };

  /**
   * Select a ply, or clear the selection when it is already selected.
   * @param {number} index
   */
  #pick(index) {
    if (index < 0) return;

    this.#touched = true;
    clearTimeout(this.#demoTimer);
    this.#selected = this.#selected === index ? -1 : index;
    this.#render();
  }

  /**
   * @param {boolean} value - Show the coated mask.
   * @param {boolean} byUser - A shopper pressed a toggle; cancels the automatic switch.
   */
  #setCoated(value, byUser) {
    if (byUser) {
      this.#touched = true;
      clearTimeout(this.#demoTimer);
    }
    if (this.#coated === value) return;

    this.#applyCoated(value);
    this.#render();
  }

  /** @param {boolean} value */
  #applyCoated(value) {
    this.#coated = value;
    this.classList.toggle('is-coated', value);

    for (const button of this.#toggles) {
      button.setAttribute('aria-pressed', String((button.dataset.coat === 'true') === value));
    }
    if (this.#note) {
      this.#note.textContent = (value ? this.#note.dataset.noteOn : this.#note.dataset.noteOff) ?? '';
    }
  }

  /** Highlights, pressed states and dimming from the current selection and coated state. */
  #render() {
    const selected = this.#selected;
    /** @param {number} index */
    const on = (index) => selected === index || (selected < 0 && this.#coated && index === COATED_PLY);
    const dim = this.dataset.dim !== 'false';

    for (const marker of this.querySelectorAll('.zg-mask-layers__marker[data-ply]')) {
      marker.classList.toggle('is-on', on(plyIndex(marker)));
    }
    for (const item of this.querySelectorAll('.zg-mask-layers__ply[data-ply]')) {
      item.classList.toggle('is-on', on(plyIndex(item)));
    }
    // Only a real selection is "pressed"; the coated highlight is visual.
    for (const button of this.querySelectorAll('button.zg-mask-layers__ply-button[data-ply]')) {
      button.setAttribute('aria-pressed', String(selected === plyIndex(button)));
    }
    for (const rect of this.querySelectorAll('.zg-mask-layers__dim[data-dim-ply]')) {
      const index = Number(rect.getAttribute('data-dim-ply'));
      rect.classList.toggle('is-dimmed', dim && selected >= 0 && selected !== index);
    }
  }
}

/**
 * The ply index in data-ply, or -1.
 * @param {Element} element
 */
function plyIndex(element) {
  const value = Number.parseInt(element.getAttribute('data-ply') ?? '', 10);
  return Number.isNaN(value) ? -1 : value;
}

if (!customElements.get('zg-mask-layers')) {
  customElements.define('zg-mask-layers', ZgMaskLayers);
}
